// components/auth/PasswordResetModal.jsx
'use client';

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';
import { FaTimes, FaSpinner } from 'react-icons/fa';

import { logger } from '@/utils/log';
export default function PasswordResetModal({ isOpen, onClose }) {
    const { requestPasswordReset, submitPasswordReset } = useAuth();
    const [step, setStep] = useState(1); // 1: request, 2: verify & reset
    const [userId, setUserId] = useState('');
    const [resetData, setResetData] = useState({
        code: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [resetMethod, setResetMethod] = useState('');
    const [availableMethods, setAvailableMethods] = useState([]);
    // Legacy RetryMethod: where a resend goes. Starts as the method the first
    // code went to (Login.razor.cs L602-616) and follows the select.
    const [retryMethod, setRetryMethod] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState({
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasMinLength: false,
        passwordsMatch: false
    });

    const validatePassword = (password, confirmPassword) => {
        setPasswordValidation({
            hasUppercase: /[A-Z]/.test(password),
            hasLowercase: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasMinLength: password.length >= 8,
            passwordsMatch: password === confirmPassword && password.length > 0
        });
    };

    const handleRequestReset = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await requestPasswordReset(userId);

            if (result.success) {
                setResetMethod(result.resetMethod);
                setAvailableMethods(result.availableMethods || []);
                setRetryMethod(result.resetMethod || '');
                setStep(2);
            } else {
                setError(result.error || 'Reset request failed');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            logger.error('Reset request error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Legacy ResendResetRequest (Login.razor.cs L618-650): the same
    // requestpasswordreset post with Method set to the selected contact; the
    // response's ResetMethod / AvailableMethods replace the current ones. Legacy
    // shows no toast here — the "A verification code has been sent to …" line
    // updating to the chosen method is the feedback — so neither does this.
    const handleResend = async () => {
        setError('');
        setLoading(true);

        try {
            const result = await requestPasswordReset(userId, retryMethod);

            if (result.success) {
                setResetMethod(result.resetMethod);
                setAvailableMethods(result.availableMethods || []);
                setRetryMethod(result.resetMethod || '');
            } else {
                setError(result.error || 'Reset request failed');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            logger.error('Reset resend error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitReset = async (e) => {
        e.preventDefault();

        if (resetData.newPassword !== resetData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const result = await submitPasswordReset(userId, resetData.code, resetData.newPassword);

            if (result.success) {
                toast.success('Password successfully updated!');
                handleClose();
            } else {
                setError(result.error || 'Reset failed');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            logger.error('Submit reset error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setStep(1);
        setUserId('');
        setResetData({ code: '', newPassword: '', confirmPassword: '' });
        setRetryMethod('');
        setError('');
        setPasswordValidation({
            hasUppercase: false,
            hasLowercase: false,
            hasNumber: false,
            hasMinLength: false,
            passwordsMatch: false
        });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/50 animate-fade-in overflow-y-auto">
            <div className="modal-content animate-scale-in w-full max-w-md" onClick={e => e.stopPropagation()}>

                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-200">
                    <h4 className="text-gray-900">Password Reset</h4>
                    <button
                        onClick={handleClose}
                        className="btn btn-ghost btn-sm p-1.5 rounded-lg"
                        disabled={loading}
                        aria-label="Close"
                    >
                        <FaTimes className="w-4 h-4" />
                    </button>
                </div>

                {step === 1 ? (
                    <form onSubmit={handleRequestReset}>
                        <div className="p-4 sm:p-5 space-y-4">
                            <p className="text-sm text-gray-600">Please enter your user ID below:</p>

                            <input
                                type="text"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                placeholder="Enter User ID here"
                                className="input-field"
                                required
                                disabled={loading}
                            />

                            {error && (
                                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 p-4 sm:p-5 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className="flex-1 btn btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 btn btn-primary"
                            >
                                {loading ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleSubmitReset}>
                        <div className="p-4 sm:p-5 space-y-4">
                            <p className="text-sm text-gray-600">
                                A verification code has been sent to {resetMethod}. Enter your one-time code below:
                            </p>

                            <input
                                type="text"
                                value={resetData.code}
                                onChange={(e) => setResetData({ ...resetData, code: e.target.value })}
                                placeholder="Enter code here"
                                className="input-field"
                                maxLength={14}
                                required
                                disabled={loading}
                            />

                            {/* Legacy Login.razor L131-142: the resend method
                                select, always shown (audit 12t item 2). The
                                leading blank option is legacy's too. */}
                            <div className="space-y-2">
                                <p className="text-sm text-gray-600">
                                    Did not receive a code? We will resend the code to your selected method below:
                                </p>
                                <select
                                    name="methods"
                                    id="selectedMethod"
                                    aria-label="Resend to selected method"
                                    value={retryMethod || ''}
                                    onChange={(e) => setRetryMethod(e.target.value)}
                                    className="input-field"
                                    disabled={loading}
                                >
                                    <option value="" disabled></option>
                                    {availableMethods.map((m) => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                            </div>

                            <hr className="border-gray-200" />

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        New Password
                                    </label>
                                    <input
                                        type="password"
                                        value={resetData.newPassword}
                                        onChange={(e) => {
                                            setResetData({ ...resetData, newPassword: e.target.value });
                                            validatePassword(e.target.value, resetData.confirmPassword);
                                        }}
                                        placeholder="New Password"
                                        className="input-field"
                                        required
                                        disabled={loading}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Confirm Password
                                    </label>
                                    <input
                                        type="password"
                                        value={resetData.confirmPassword}
                                        onChange={(e) => {
                                            setResetData({ ...resetData, confirmPassword: e.target.value });
                                            validatePassword(resetData.newPassword, e.target.value);
                                        }}
                                        placeholder="Confirm Password"
                                        className="input-field"
                                        required
                                        disabled={loading}
                                    />
                                </div>

                                {/* Password validation */}
                                <div className="text-sm space-y-1">
                                    <p className={passwordValidation.hasUppercase ? 'text-green-600' : 'text-gray-500'}>
                                        {passwordValidation.hasUppercase ? '✓' : '•'} At least one uppercase letter
                                    </p>
                                    <p className={passwordValidation.hasLowercase ? 'text-green-600' : 'text-gray-500'}>
                                        {passwordValidation.hasLowercase ? '✓' : '•'} At least one lowercase letter
                                    </p>
                                    <p className={passwordValidation.hasNumber ? 'text-green-600' : 'text-gray-500'}>
                                        {passwordValidation.hasNumber ? '✓' : '•'} At least one number
                                    </p>
                                    <p className={passwordValidation.hasMinLength ? 'text-green-600' : 'text-gray-500'}>
                                        {passwordValidation.hasMinLength ? '✓' : '•'} At least 8 characters
                                    </p>
                                    <p className={passwordValidation.passwordsMatch ? 'text-green-600' : 'text-gray-500'}>
                                        {passwordValidation.passwordsMatch ? '✓' : '•'} Passwords match
                                    </p>
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 p-4 sm:p-5 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className="flex-1 btn btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleResend}
                                disabled={loading || !retryMethod}
                                className="flex-1 btn btn-outline"
                            >
                                Resend
                            </button>
                            <button
                                type="submit"
                                disabled={loading || !Object.values(passwordValidation).every(v => v)}
                                className="flex-1 btn btn-primary"
                            >
                                {loading ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}